import {
  useRef, useState, useCallback, useEffect,
} from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import api from 'api';
import { reducePath } from './useIdeaEditor';

export const useAssessmentEditorHook = (idea, onAssessmentEdited = () => {}) => {
  const [assessmentState, setAssessmentState] = useState({
    assessment: {},
    isLoading: false,
    isSaving: false,
    savedAt: {},
  });
  const ideaId = idea?._id;
  const isAssessor = idea?.isAssessor;
  const editTimeout = useRef(null);

  const getAssessment = useCallback(() => {
    if (isAssessor) {
      setAssessmentState((prevState) => ({ ...prevState, isLoading: true }));
      api.ideas.getAssessment(ideaId, (newAssessment) => {
        setAssessmentState((prevState) => ({ ...prevState, isLoading: false, assessment: newAssessment }));
      }, () => setAssessmentState((prevState) => ({ ...prevState, isLoading: false })));
    }
  }, [ideaId, isAssessor]);

  useEffect(() => getAssessment(), [getAssessment]);

  const saveAssessment = useCallback((sectionId, changes) => new Promise(() => {
    setAssessmentState((prevState) => ({ ...prevState, isSaving: true }));
    return api.ideas.updateAssessment(ideaId, changes, () => {
      setAssessmentState((prevState) => ({
        ...prevState,
        isSaving: false,
        assessmentSavedAt: { ...prevState.assessmentSavedAt, [sectionId]: moment() },
      }));
    }, (err) => {
      toast.error(err.message);
      setAssessmentState((prevState) => ({ ...prevState, isSaving: false }));
    });
  }), [ideaId]);

  /*
    Path is as follows: ['assessment_object', 'section_id_here', 'field']
  */
  const updateAssessment = useCallback((update, path, suppressSave = false) => {
    setAssessmentState((prevState) => {
      const prevAssessment = prevState.assessment || {};
      const updatedAssessment = reducePath(prevAssessment, update, path);

      if (editTimeout.current) {
        clearTimeout(editTimeout.current);
        editTimeout.current = null;
      }
      editTimeout.current = setTimeout(() => {
        // Second item in the path will be the section id, if saving
        if (!suppressSave) saveAssessment(path[1], updatedAssessment);
      }, 400);

      onAssessmentEdited();

      return {
        ...prevState,
        assessment: updatedAssessment,
      };
    });
  }, [saveAssessment, onAssessmentEdited]);

  return [assessmentState, updateAssessment];
};

export default useAssessmentEditorHook;
